import { Component, computed, input, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { InspectionItem, InspectionItemAttachment } from '@raptor-fe/core';
import { TranslationPipe } from '@raptor-fe/services';

@Component({
    selector: 'lib-inspection-attachments-popup',
    imports: [TranslationPipe],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './inspection-attachments-popup.component.html',
})
export class InspectionAttachmentsPopupComponent {
    selectedItem = input<InspectionItem | null>(null);
    popupType = input<'photo' | 'video' | null>(null);
    visible = input<boolean>(false);
    isMediaType = input.required<(fileName: string | undefined) => 'photo' | 'video' | 'unknown'>();

    /**
     * Opt-in delete affordance. Default off so every existing read-only consumer (including
     * mobile) renders exactly as it does today and never surfaces the delete controls.
     */
    canDelete = input<boolean>(false);

    closed = output<void>();
    deleteRequested = output<InspectionItemAttachment>();

    protected showImagePopup = false;
    protected imagePopupUrl: string | undefined = undefined;

    protected pendingDelete = signal<InspectionItemAttachment | null>(null);

    protected requestDelete(file: InspectionItemAttachment): void {
        this.pendingDelete.set(file);
    }

    protected cancelDelete(): void {
        this.pendingDelete.set(null);
    }

    protected confirmDelete(): void {
        const file = this.pendingDelete();
        if (file) {
            this.deleteRequested.emit(file);
        }
        this.pendingDelete.set(null);
    }

    /**
     * The video a Tech Check item cannot lose. Transfer needs at least one .mp4 on the Tech Check item and
     * the advisor cannot record a replacement, so when only one is left its delete control is withheld and
     * the advisor is pointed at reopening for the technician instead.
     */
    protected readonly lastTechCheckVideoId = computed(() => {
        const item = this.selectedItem();
        if (!item?.isTechCheck) {
            return null;
        }
        const mp4s = (item.attachments ?? []).filter((a) => (a.name ?? a.internalName ?? '').toLowerCase().endsWith('.mp4'));
        return mp4s.length === 1 ? mp4s[0].id : null;
    });

    /**
     * Resolves "photo" / "video" for the pending attachment so the confirmation copy names what
     * is actually being destroyed. The attachment kind is derived through the existing
     * isMediaType input rather than a stored kind field.
     */
    protected readonly noun = computed(() =>
        this.isMediaType()(this.pendingDelete()?.internalName) === 'video' ? 'video' : 'photo',
    );
}
